<script setup lang="ts">
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import useLocale from '../../composables/useLocale';
import useUiStore from '../../stores/ui';
import useReaderStore from '../../stores/reader';
import { FontAwesomeIcon, FontAwesomeLayers } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faList, faBarsStaggered } from '@fortawesome/pro-solid-svg-icons';
import { useDisplay } from 'vuetify';

library.add([faList, faBarsStaggered]);

type Props = {
  btnSize?: number;
  showTooltips: boolean;
};

withDefaults(defineProps<Props>(), {
  btnSize: 48,
});

const { t } = useLocale();
const uiStore = useUiStore();
const { toggleTableOfContentDrawer, closeTableOfContentDrawer } = uiStore;
const { isTableOfContentVisible } = storeToRefs(uiStore);
const { isAudioOnly } = storeToRefs(useReaderStore());
const { smAndDown, sm } = useDisplay();

const tocBtn = ref<HTMLButtonElement | null>(null);

const escapeButtonHandler = (e: KeyboardEvent) => {
  // Only run if TOC is hidden.
  if (isTableOfContentVisible.value) {
    // Prevent other escape button listeners to be triggered.
    e.preventDefault();
    e.stopPropagation();
    closeTableOfContentDrawer();
  }
};

const tooltipLabel = computed(() => (!isAudioOnly.value ? 'button_table_of_contents' : 'book_player.button_playlist'));
</script>

<template>
  <v-btn
    id="tableOfContentsBottomBtn"
    data-testid="bp-table-of-content-btn"
    ref="tocBtn"
    :icon="!isAudioOnly"
    :title="isAudioOnly ? t('book_player.button_playlist') : t('title_table_of_contents')"
    variant="text"
    color="on-primary"
    :ripple="false"
    @click="toggleTableOfContentDrawer(!isTableOfContentVisible)"
    @keydown.esc="escapeButtonHandler"
    :height="btnSize"
    :width="isAudioOnly && !smAndDown ? '190' : isAudioOnly && sm ? '120' : btnSize"
    :slim="isAudioOnly && smAndDown"
  >
    <div :class="{ 'd-flex flex-column flex-md-row align-center ga-2 overflow-hidden': isAudioOnly }">
      <FontAwesomeIcon v-if="!isTableOfContentVisible" size="xl" icon="fa-solid fa-list" flip="horizontal" />
      <FontAwesomeLayers v-else class="ml-n2">
        <FontAwesomeIcon size="xl" icon="fa-solid fa-bars-staggered" flip="horizontal" />
        <FontAwesomeIcon size="lg" icon="fa-solid fa-chevron-left" transform="shrink-3 right-16" />
      </FontAwesomeLayers>
      <span
        v-if="isAudioOnly"
        class="text-truncate"
        :class="[smAndDown ? 'text-body-2 w-100' : 'text-body-1', { 'ml-4': isTableOfContentVisible }]"
      >
        {{ t('book_player.button_playlist') }}
      </span>
    </div>
    <v-tooltip :disabled="!showTooltips" activator="parent" location="top">
      {{ t(tooltipLabel) }}
    </v-tooltip>
  </v-btn>
</template>

<style lang="scss" scoped></style>
